import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/nicknames")({
  component: AdminNicknames,
});

interface Row {
  id: string;
  user_id: string;
  current_nickname: string;
  requested_nickname: string;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  reviewed_at: string | null;
  email?: string | null;
}

function AdminNicknames() {
  const [tab, setTab] = useState<"pending" | "history">("pending");
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    const { data } = await supabase
      .from("nickname_requests" as any)
      .select("id, user_id, current_nickname, requested_nickname, status, created_at, reviewed_at")
      .order("created_at", { ascending: false });
    const list = (data as any as Row[]) ?? [];
    const ids = list.map((r) => r.user_id);
    if (ids.length) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, email")
        .in("id", ids);
      const map = new Map((profs ?? []).map((p: any) => [p.id, p.email]));
      list.forEach((r) => { r.email = map.get(r.user_id) ?? null; });
    }
    setRows(list);
  };

  useEffect(() => { load(); }, []);

  const approve = async (id: string) => {
    setBusy(id);
    const { error } = await supabase.rpc("admin_approve_nickname_change" as any, { request_id: id });
    setBusy(null);
    if (error) {
      const msg = error.message.includes("nickname_taken")
        ? "El nickname ya está ocupado — rechaza la solicitud o pídele otro."
        : error.message;
      return toast.error(msg);
    }
    toast.success("Nickname actualizado");
    load();
  };

  const reject = async (id: string) => {
    setBusy(id);
    const { error } = await supabase.rpc("admin_reject_nickname_change" as any, { request_id: id });
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success("Solicitud rechazada");
    load();
  };

  const pending = rows.filter((r) => r.status === "pending");
  const history = rows.filter((r) => r.status !== "pending");
  const list = tab === "pending" ? pending : history;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl mb-1">Solicitudes de nickname</h1>
        <p className="text-sm text-muted-foreground">Revisa cambios de nombre pedidos por los usuarios.</p>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setTab("pending")}
          className={`rounded-full px-4 py-1.5 text-sm ${tab === "pending" ? "bg-[#F5C518] text-black font-semibold" : "bg-[#1a1a1a] text-white/70 border border-border"}`}
        >
          Pendientes ({pending.length})
        </button>
        <button
          onClick={() => setTab("history")}
          className={`rounded-full px-4 py-1.5 text-sm ${tab === "history" ? "bg-[#F5C518] text-black font-semibold" : "bg-[#1a1a1a] text-white/70 border border-border"}`}
        >
          Historial ({history.length})
        </button>
      </div>

      <div className="rounded-2xl border border-border bg-[#1a1a1a] divide-y divide-border">
        {list.length === 0 ? (
          <div className="p-6 text-center text-sm text-white/60">
            {tab === "pending" ? "No hay solicitudes pendientes." : "Sin historial."}
          </div>
        ) : list.map((r) => (
          <div key={r.id} className="flex flex-col md:flex-row md:items-center gap-3 p-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-white/60 line-through">@{r.current_nickname}</span>
                <span className="text-white/40">→</span>
                <span className="font-semibold text-[#F5C518]">@{r.requested_nickname}</span>
              </div>
              <div className="mt-1 text-[11px] text-white/50 truncate">
                {r.email ?? r.user_id} · {new Date(r.created_at).toLocaleString("es-ES")}
                {r.status !== "pending" && r.reviewed_at && (
                  <> · {r.status === "approved" ? "Aprobada" : "Rechazada"} el {new Date(r.reviewed_at).toLocaleDateString("es-ES")}</>
                )}
              </div>
            </div>
            {r.status === "pending" ? (
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => approve(r.id)}
                  disabled={busy === r.id}
                  className="flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                >
                  <Check className="h-3.5 w-3.5" /> Aprobar
                </button>
                <button
                  onClick={() => reject(r.id)}
                  disabled={busy === r.id}
                  className="flex items-center gap-1 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                >
                  <X className="h-3.5 w-3.5" /> Rechazar
                </button>
              </div>
            ) : (
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                r.status === "approved"
                  ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                  : "bg-red-500/15 text-red-400 border border-red-500/30"
              }`}>
                {r.status === "approved" ? "Aprobada" : "Rechazada"}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
